import { useState, useEffect, useRef } from 'react'
import { Props_SnippetVideo } from './Video.props'
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SnippetLink from '../Shared/Link/Link'

type Video = 'youtube' | 'vimeo' | 'video' | null

const setAutoplay = (url: string) => {
    const hasAutoplay = url.includes("autoplay=1");
    if (!hasAutoplay) {
        // Handle YouTube watch URLs - convert to embed format
        const youtubeWatchScript = url.includes("youtube") && !url.includes("embed");
        if (youtubeWatchScript) {
            const videoIdMatch = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:&|$)/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
            }
        }

        // Handle Vimeo URLs - always convert to player embed format
        if (url.includes("vimeo")) {
            const videoIdMatch = url.match(/vimeo\.com\/(?:.*\/)?(\d+)(?:[?#].*)?$/);
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            
            if (videoId) {
                const embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
                return  embedUrl;
            }
        }

        // For other URLs, append autoplay parameter
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}autoplay=1`;
    }
    return url;
};
const getVideoMimeType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
            return 'video/mp4'
        case 'webm':
            return 'video/webm'
        case 'ogg':
        case 'ogv':
            return 'video/ogg'
        case 'mov':
            return 'video/quicktime'
        default:
            return 'video/mp4'
    }
}

const getVideoType = (url: string): Video => {
    if (!url) return null
    if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube'
    if (url.includes('vimeo')) return 'vimeo'
    return 'video'
}

const SnippetVideo = (props: Props_SnippetVideo) => {
    const { heading, content, repeatable_link, video, linkProps } = props

    const [videoType, setVideoType] = useState<Video>(null)
    const [playing, setPlaying] = useState<boolean>(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const classHeadingVisibility = heading.visibility?.value ? '' : 'snippet-video__heading--hidden'
    const classHeadingAlignment = heading.alignment?.value ? `snippet-video__heading--alignment-${heading.alignment.value}` : ''
    const classContentPosition = content.position?.value ? `snippet-video--position-${content.position.value}` : ''
    const classContentAlignment = content.alignment?.value ? `snippet-video--alignment-${content.alignment.value}` : ''
    const classVideoPlaying = playing ? 'snippet-video--playing' : ''
    const classVideoFullscreen = video.fullscreen?.value ? 'snippet-video--fullscreen' : ''
    const classLinkAlignment = linkProps.alignment?.value ? `snippet-video__container-links--${linkProps.alignment.value}` : ''

    useEffect(() => {
        setVideoType(video?.url?.value ? getVideoType(video.url.value) : null)
    }, [playing, videoType])

    return (
        <section className={`snippet-video ${classVideoPlaying} ${classVideoFullscreen} ${classContentPosition} ${classContentAlignment}`}>
            <div className="snippet-video__container">
                <div className="snippet-video__container-content">
                    <h2 className={`snippet-video__heading ${classHeadingVisibility} ${classHeadingAlignment}`}>{heading?.content?.value}</h2>
                    <div className="snippet-video__content">
                        {content.content?.value && Legacy_CompileMarkdown(content?.content?.value)}
                    </div>

                    <div className={`snippet-video__container-links ${classLinkAlignment}`}>
                        {repeatable_link?.map(({ linkHref, linkText, linkStyle, linkTarget }, index) => (

                            <SnippetLink
                                key={`link-${index}`}
                                url={linkHref?.value}
                                className="snippet-video__link"
                                linkStyles={linkStyle?.value}
                                openInNewTab={linkTarget?.value}
                            >
                                {linkText?.value}
                            </SnippetLink>
                        ))}
                    </div>

                </div>

                <div className="snippet-video__container-video-player">
                    {video?.thumbnail?.value && (
                        <figure className="snippet-video__container-thumbnail">
                            <img
                                src={video.thumbnail.value}
                                alt={video?.alt?.value || ''}
                                className="snippet-video__thumbnail"
                                loading="lazy"
                                width="1280"
                                height="720"
                            />
                        </figure>
                    )}
                    {video?.url?.value && (
                        <div className="snippet-video__container-video">
                            {video?.thumbnail?.value && (
                                <button
                                    className="snippet-video__button-play"
                                    aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                    onClick={() => setPlaying(true)}
                                ></button>
                            )}
                            {playing && (videoType === 'youtube' || videoType === 'vimeo') && (
                                <iframe
                                    className="snippet-video__video"
                                    src={setAutoplay(video.url.value)}
                                    width="1280"
                                    height="720"
                                    title="Video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy={videoType === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                                    allowFullScreen
                                ></iframe>
                            )}
                            {playing && videoType === 'video' && (
                                <video
                                    className="snippet-video__video"
                                    controls
                                    controlsList='nodownload'
                                    width="1280"
                                    height="720"
                                    ref={videoRef}
                                    autoPlay
                                >
                                    <track kind="captions" />
                                    <source src={video.url.value} type={getVideoMimeType(video.url.value)} />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default SnippetVideo